/**
 * Extracts a dominant brand color from an image URL (data URI or http).
 * Skips near-white, near-black, and grey pixels so the result is a real
 * saturated brand colour. Returns a hex string. Falls back to a fuchsia
 * accent if nothing is found.
 */
export async function extractDominantColor(imageUrl, fallback = "#7c3aed") {
  if (!imageUrl) return fallback;
  return new Promise((resolve) => {
    const img = new Image();
    if (!imageUrl.startsWith("data:")) img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const size = 48;
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        const buckets = {};
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
          if (a < 200) continue;
          const max = Math.max(r, g, b), min = Math.min(r, g, b);
          const lum = (max + min) / 2;
          if (lum < 30 || lum > 225) continue;
          if (max - min < 25) continue;
          const key = `${r >> 5},${g >> 5},${b >> 5}`;
          if (!buckets[key]) buckets[key] = { count: 0, r: 0, g: 0, b: 0 };
          buckets[key].count++;
          buckets[key].r += r; buckets[key].g += g; buckets[key].b += b;
        }
        const sorted = Object.values(buckets).sort((a, b) => b.count - a.count);
        if (sorted.length === 0) return resolve(fallback);
        const top = sorted[0];
        const r = Math.round(top.r / top.count);
        const g = Math.round(top.g / top.count);
        const b = Math.round(top.b / top.count);
        resolve(rgbToHex(r, g, b));
      } catch { resolve(fallback); }
    };
    img.onerror = () => resolve(fallback);
    img.src = imageUrl;
  });
}

function rgbToHex(r, g, b) {
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

/** Darken a hex color by a percentage (0-1). */
export function darken(hex, amount = 0.18) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  return rgbToHex(Math.round(r * f), Math.round(g * f), Math.round(b * f));
}

/** Lighten a hex color by a percentage (0-1). */
export function lighten(hex, amount = 0.85) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(
    Math.round(r + (255 - r) * amount),
    Math.round(g + (255 - g) * amount),
    Math.round(b + (255 - b) * amount),
  );
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}
